import { useEffect, useState } from "react";
import { BadgeCheck, CreditCard, FilePlus2, PackageCheck } from "lucide-react";
import { useForm, useWatch } from "react-hook-form";
import { zodResolver } from "@hookform/resolvers/zod";
import { z } from "zod";
import { useAuth } from "../auth/AuthProvider";
import { useOrganization } from "../organization/useOrganization";
import { Button } from "../../shared/ui/Button";
import { Card, EmptyState } from "../../shared/ui/Card";
import { PageSkeleton, StatusBadge } from "../../shared/ui/Status";
import type {
  CourseProduct,
  CreateOrderInput,
  SalesOrder,
} from "./sales.types";
import { useSales, useSalesActions } from "./useSales";
import { RefundRequestPanel } from "../refunds/RefundRequestPanel";
import { money } from "./salesFormat";

const orderSchema = z.object({
  memberId: z.string().uuid("请选择会员"),
  productId: z.string().uuid("请选择产品"),
  finalPriceYuan: z.number().positive("金额必须大于0"),
  giftLessons: z.number().int().min(0),
  exceptionReason: z.string(),
});

export function SalesWorkbench() {
  const { session } = useAuth();
  const organization = useOrganization();
  const sales = useSales();
  const actions = useSalesActions();
  const [notice, setNotice] = useState("");
  const [error, setError] = useState("");
  const form = useForm<CreateOrderInput>({
    resolver: zodResolver(orderSchema),
    defaultValues: {
      memberId: "",
      productId: "",
      finalPriceYuan: 0,
      giftLessons: 0,
      exceptionReason: "",
    },
  });
  const productId = useWatch({ control: form.control, name: "productId" });
  useEffect(() => {
    const product = sales.data?.products.find((item) => item.id === productId);
    if (product)
      form.setValue("finalPriceYuan", product.standardPriceCents / 100);
  }, [form, productId, sales.data?.products]);
  if (!session || !organization.data || !sales.data) return <PageSkeleton />;
  const members = organization.data.accounts.filter(
    (item) =>
      item.role === "member" &&
      item.status === "active" &&
      item.storeId === session.profile.storeId,
  );
  const products = sales.data.products.filter(
    (item) =>
      item.active &&
      (item.scope === "company" || item.storeId === session.profile.storeId),
  );
  const orders = sales.data.orders.filter(
    (item) => item.storeId === session.profile.storeId,
  );
  const run = async (action: () => Promise<unknown>, success: string) => {
    setError("");
    setNotice("");
    try {
      await action();
      setNotice(success);
    } catch (cause) {
      setError(cause instanceof Error ? cause.message : "操作失败");
    }
  };
  const submit = async (input: CreateOrderInput) => {
    await run(
      () => actions.createOrder(input),
      "订单已创建。标准价订单可直接收款，例外订单已进入店长待办。",
    );
    form.reset({
      memberId: "",
      productId: "",
      finalPriceYuan: 0,
      giftLessons: 0,
      exceptionReason: "",
    });
  };
  return (
    <section className="sales-workbench page-stack">
      {(notice || error) && (
        <div
          className={
            error ? "inline-notice inline-notice--error" : "inline-notice"
          }
        >
          {error || notice}
        </div>
      )}
      <div className="management-grid">
        <Card className="management-card">
          <div className="card-heading">
            <FilePlus2 />
            <div>
              <span>{session.profile.role === "manager" ? "店长录单" : "员工录单"}</span>
              <h2>创建购课订单</h2>
            </div>
          </div>
          <form className="v4-form" onSubmit={form.handleSubmit(submit)}>
            <label className="v4-field">
              <span>会员</span>
              <select {...form.register("memberId")}>
                <option value="">请选择本店会员</option>
                {members.map((m) => (
                  <option key={m.id} value={m.id}>
                    {m.name} · {m.phone}
                  </option>
                ))}
              </select>
              {form.formState.errors.memberId && (
                <small>{form.formState.errors.memberId.message}</small>
              )}
            </label>
            <label className="v4-field">
              <span>课程产品</span>
              <select {...form.register("productId")}>
                <option value="">请选择</option>
                {products.map((p) => (
                  <option key={p.id} value={p.id}>
                    {p.name} · {money(p.standardPriceCents)}
                  </option>
                ))}
              </select>
            </label>
            <div className="form-split">
              <label className="v4-field">
                <span>成交金额（元）</span>
                <input
                  type="number"
                  step="0.01"
                  {...form.register("finalPriceYuan", { valueAsNumber: true })}
                />
              </label>
              <label className="v4-field">
                <span>赠送课时</span>
                <input
                  type="number"
                  min="0"
                  {...form.register("giftLessons", { valueAsNumber: true })}
                />
              </label>
            </div>
            <label className="v4-field">
              <span>优惠/改价/赠课原因</span>
              <input
                {...form.register("exceptionReason")}
                placeholder="标准价且无赠课时可留空"
              />
            </label>
            <Button disabled={actions.pending || !members.length}>
              <FilePlus2 size={16} />
              提交订单
            </Button>
          </form>
          {!members.length && (
            <p className="field-error">请先在本页上方创建本店会员。</p>
          )}
        </Card>
        <Card className="management-card sales-rule-card">
          <BadgeCheck />
          <h2>价格与审批规则</h2>
          <p>
            标准价、无赠课的订单直接进入“已确认”；任何优惠、赠课或手工改价都必须填写原因并由本店店长确认。订单永久保存产品和价格快照。
          </p>
        </Card>
      </div>
      <OrderList
        orders={orders}
        products={products}
        memberNames={Object.fromEntries(members.map((m) => [m.id, m.name]))}
        canPay
        pending={actions.pending}
        onPay={(orderId, method) =>
          window.confirm("确认已收到该笔款项？确认后将立即开通课程包。") &&
          run(
            () =>
              actions.confirmPayment({
                orderId,
                method,
                idempotencyKey: crypto.randomUUID(),
              }),
            "收款成功，课程包已原子开通。",
          )
        }
      />
      <RefundRequestPanel />
    </section>
  );
}

export function OrderList({
  orders,
  memberNames,
  canPay = false,
  pending = false,
  onPay,
}: {
  orders: SalesOrder[];
  products?: CourseProduct[];
  memberNames: Record<string, string>;
  canPay?: boolean;
  pending?: boolean;
  onPay?: (id: string, method: "wechat" | "alipay" | "bank" | "cash") => void;
}) {
  const [methods, setMethods] = useState<
    Record<string, "wechat" | "alipay" | "bank" | "cash">
  >({});
  if (!orders.length)
    return (
      <EmptyState
        title="暂无订单"
        description="创建第一笔标准价或例外订单后会显示在这里。"
      />
    );
  return (
    <Card className="directory-card">
      <div className="card-heading">
        <CreditCard />
        <div>
          <span>价格快照不可覆盖</span>
          <h2>订单与收款</h2>
        </div>
      </div>
      <div className="order-list">
        {orders.map((order) => (
          <article key={order.id}>
            <div className="order-main">
              <span>{order.orderNumber}</span>
              <h3>
                {memberNames[order.memberId] ?? "会员"} ·{" "}
                {order.pricing.productName}
              </h3>
              <p>
                标准价 {money(order.pricing.standardPriceCents)} · 成交{" "}
                {money(order.pricing.finalPriceCents)} ·{" "}
                {order.pricing.lessonCount}+{order.pricing.giftLessons}课时
              </p>
            </div>
            <StatusBadge
              tone={
                order.status === "paid"
                  ? "success"
                  : order.status === "pending_approval"
                    ? "warning"
                    : order.status === "cancelled"
                      ? "danger"
                      : "neutral"
              }
            >
              {statusLabel(order.status)}
            </StatusBadge>
            {order.exceptionReason && (
              <p className="order-reason">原因：{order.exceptionReason}</p>
            )}
            {canPay && order.status === "confirmed" && (
              <div className="payment-action">
                <select
                  value={methods[order.id] ?? "wechat"}
                  onChange={(e) =>
                    setMethods({
                      ...methods,
                      [order.id]: e.target.value as
                        "wechat" | "alipay" | "bank" | "cash",
                    })
                  }
                >
                  <option value="wechat">微信</option>
                  <option value="alipay">支付宝</option>
                  <option value="bank">银行转账</option>
                  <option value="cash">现金</option>
                </select>
                <Button
                  disabled={pending}
                  onClick={() =>
                    onPay?.(order.id, methods[order.id] ?? "wechat")
                  }
                >
                  <PackageCheck size={15} />
                  确认收款并开课包
                </Button>
              </div>
            )}
          </article>
        ))}
      </div>
    </Card>
  );
}

export { money };
export function statusLabel(status: SalesOrder["status"]) {
  return {
    draft: "草稿",
    pending_approval: "待审批",
    confirmed: "已确认",
    paid: "已收款",
    partially_refunded: "部分退款",
    refunded: "已退款",
    cancelled: "已取消",
  }[status];
}
